import { useCallback, useEffect, useState } from 'react';

function storageKey(orderId: string): string {
  return `loupe:collapsed-rolls:${orderId}`;
}

// Storage can be missing or throw (private browsing, blocked site data); the
// gallery then just opens with every roll expanded.
function read(orderId: string): Set<string> {
  try {
    const ids: unknown = JSON.parse(sessionStorage.getItem(storageKey(orderId)) ?? '[]');
    return new Set(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

function write(orderId: string, ids: Set<string>): void {
  try {
    if (ids.size === 0) sessionStorage.removeItem(storageKey(orderId));
    else sessionStorage.setItem(storageKey(orderId), JSON.stringify([...ids]));
  } catch {
    // Not remembered; nothing else depends on it.
  }
}

/**
 * Which of an order's rolls the customer has collapsed. Every roll starts
 * expanded; the choice lasts for the browser tab, so coming back from the
 * editor or the print basket finds the gallery as it was left.
 */
export function useCollapsedRolls(orderId: string) {
  const [collapsed, setCollapsed] = useState(() => read(orderId));

  useEffect(() => write(orderId, collapsed), [orderId, collapsed]);

  const toggle = useCallback((rollId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (!next.delete(rollId)) next.add(rollId);
      return next;
    });
  }, []);

  const collapseAll = useCallback((rollIds: string[]) => setCollapsed(new Set(rollIds)), []);
  const expandAll = useCallback(() => setCollapsed(new Set()), []);

  return { collapsed, toggle, collapseAll, expandAll };
}
